import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Text, VStack, Button, HStack, IconButton } from 'src/components/ui';
import { FaMousePointer } from 'react-icons/fa';
import { CloseIcon } from '@chakra-ui/icons';
import {
  RIGHT_SIDEBAR_WIDTH_COLLAPSED,
  TOP_BAR_HEIGHT,
} from 'src/components/campaigns/utils/constants';

interface GuidedCursorStep {
  targetId: string;
  message: string;
  subMessage?: string;
}

interface GuidedCursorProps {
  isActive: boolean;
  steps: GuidedCursorStep[];
  onComplete: () => void;
  onSkip: () => void;
}

/**
 * GuidedCursor Component
 *
 * Shows an animated cursor guiding users to click specific elements.
 * Features:
 * - Cursor animates from user's position to target
 * - Click ripple animation
 * - Skip button
 * - Auto-closes if user navigates away
 */
const GuidedCursor: React.FC<GuidedCursorProps> = ({
  isActive,
  steps,
  onComplete,
  onSkip,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showClickEffect, setShowClickEffect] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });

  const initialPathRef = useRef<string | null>(null); // Will be set on activation, not mount
  const clickHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const isInitializedRef = useRef(false); // Track if we've been initialized this activation

  const currentStep = steps[currentStepIndex];

  // Track user's mouse position for animation start
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isAnimating) {
        setStartPosition({ x: e.clientX, y: e.clientY });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isAnimating]);

  // Close guided cursor if user navigates away
  // IMPORTANT: Only check AFTER initialization is complete (isInitializedRef is true)
  useEffect(() => {
    // Don't check navigation until we're fully initialized
    if (!isActive || !isInitializedRef.current || !initialPathRef.current) {
      return;
    }

    if (location.pathname !== initialPathRef.current) {
      console.log('🎯 [GuidedCursor] User navigated away, skipping. Current:', location.pathname, 'Initial:', initialPathRef.current);
      onSkip();
    }
  }, [location.pathname, isActive, onSkip]);

  // Find and position the cursor to target element
  const animateToTarget = useCallback(() => {
    console.log('🎯 [GuidedCursor] animateToTarget called', {
      currentStep: currentStep?.targetId,
      isActive,
      stepsLength: steps.length,
      currentStepIndex,
    });

    if (!currentStep) {
      console.log('🎯 [GuidedCursor] animateToTarget skipped - no currentStep');
      return;
    }

    if (!isActive) {
      console.log('🎯 [GuidedCursor] animateToTarget skipped - not active');
      return;
    }

    console.log('🎯 [GuidedCursor] animateToTarget proceeding for:', currentStep.targetId);

    const findAndAnimateToElement = () => {
      const targetElement = document.getElementById(currentStep.targetId);
      console.log('🎯 [GuidedCursor] Finding element:', currentStep.targetId, '- found:', !!targetElement);

      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setTargetRect(rect);

        // Start from user's cursor position (or center if not tracked)
        const start = startPosition.x === 0 && startPosition.y === 0
          ? { x: window.innerWidth / 2, y: window.innerHeight / 2 }
          : startPosition;

        // Set initial position
        setCursorPosition(start);
        setIsVisible(true);
        setIsAnimating(true);

        // Animate to target after a brief delay
        setTimeout(() => {
          setCursorPosition({
            x: rect.left + rect.width / 2 - 12,
            y: rect.top + rect.height / 2 - 12,
          });
        }, 100);

        // Show click effect after cursor arrives
        setTimeout(() => {
          setShowClickEffect(true);
          setTimeout(() => setShowClickEffect(false), 600);
        }, 800);

        return true;
      }
      return false;
    };

    // Try to find the element immediately
    if (!findAndAnimateToElement()) {
      // If not found, set up observer for dynamically rendered elements
      observerRef.current = new MutationObserver(() => {
        if (findAndAnimateToElement()) {
          observerRef.current?.disconnect();
        }
      });

      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Clean up observer after 3 seconds if element never found
      setTimeout(() => {
        observerRef.current?.disconnect();
      }, 3000);
    }
  }, [currentStep, isActive, startPosition]);

  // Handle advancing to next step (used by both click on target and Next button)
  const advanceToNextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setIsAnimating(false);
      setIsVisible(false);
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      setIsVisible(false);
      onComplete();
    }
  }, [currentStepIndex, steps.length, onComplete]);

  // Handle going back to previous step
  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      // If going back from step 2 to step 1, collapse the Ad Campaigns menu first
      if (currentStepIndex === 1) {
        // Dispatch event to collapse all menus so step 1 shows correctly
        window.dispatchEvent(new CustomEvent('collapseAllMenus'));
      }
      setIsAnimating(false);
      setIsVisible(false);
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  // Handle Next button click - simulate clicking the target element
  const handleNextClick = useCallback(() => {
    const targetElement = document.getElementById(currentStep?.targetId || '');
    if (targetElement && targetRect) {
      // Create and dispatch a proper mouse event with valid coordinates
      // This is needed because our click blocker checks clientX/clientY
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: targetRect.left + targetRect.width / 2,
        clientY: targetRect.top + targetRect.height / 2
      });
      targetElement.dispatchEvent(clickEvent);
    }
  }, [currentStep?.targetId, targetRect]);

  // Set up click handler for target element
  useEffect(() => {
    if (!currentStep || !isActive || !targetRect) return;

    const targetElement = document.getElementById(currentStep.targetId);
    if (!targetElement) return;

    const handleClick = () => {
      advanceToNextStep();
    };

    clickHandlerRef.current = handleClick;
    targetElement.addEventListener('click', handleClick);

    return () => {
      if (clickHandlerRef.current) {
        targetElement.removeEventListener('click', clickHandlerRef.current);
      }
    };
  }, [currentStep, isActive, targetRect, advanceToNextStep]);

  // Start animation when step changes (but NOT on initial activation - that's handled separately)
  const prevStepIndexRef = useRef(currentStepIndex);
  useEffect(() => {
    // Only run when step actually changes (not on initial mount)
    if (prevStepIndexRef.current === currentStepIndex) {
      return;
    }

    console.log('🎯 [GuidedCursor] Step changed from', prevStepIndexRef.current, 'to', currentStepIndex);
    prevStepIndexRef.current = currentStepIndex;

    if (isActive && !isAnimating) {
      // Reset state and animate to new target
      setIsVisible(false);
      setTimeout(() => {
        animateToTarget();
      }, 300);
    }
  }, [currentStepIndex, isActive, animateToTarget, isAnimating]);

  // Track if we've already initialized to prevent double animation
  const hasInitializedRef = useRef(false);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize on activation - separate from animateToTarget to avoid dependency issues
  useEffect(() => {
    // Cleanup function
    const cleanup = () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      observerRef.current?.disconnect();
    };

    if (isActive) {
      // Prevent double initialization
      if (hasInitializedRef.current) {
        console.log('🎯 [GuidedCursor] Already initialized, skipping');
        return cleanup;
      }

      console.log('🎯 [GuidedCursor] Initializing guided cursor, isActive:', isActive, 'path:', location.pathname);

      // IMPORTANT: Set refs BEFORE setting hasInitializedRef to prevent race conditions
      initialPathRef.current = location.pathname;
      hasInitializedRef.current = true;

      // Reset state
      setCurrentStepIndex(0);
      setIsAnimating(false);
      setIsVisible(false);

      // Mark as fully initialized AFTER a microtask to ensure state is committed
      // This allows the navigation check effect to properly guard against premature execution
      Promise.resolve().then(() => {
        isInitializedRef.current = true;
        console.log('🎯 [GuidedCursor] Fully initialized, isInitializedRef set to true');
      });

      // Use a longer delay and ensure we're not cancelled
      initTimeoutRef.current = setTimeout(() => {
        console.log('🎯 [GuidedCursor] Timeout fired, calling animateToTarget');
        // Check again that we're still active
        if (hasInitializedRef.current) {
          animateToTarget();
        }
      }, 600);
    } else {
      // Reset when deactivated
      console.log('🎯 [GuidedCursor] Deactivating, resetting state');
      hasInitializedRef.current = false;
      isInitializedRef.current = false;
      initialPathRef.current = null;
      setIsVisible(false);
      setCurrentStepIndex(0);
      setIsAnimating(false);
      cleanup();
    }

    return cleanup;
  }, [isActive, location.pathname]); // Removed animateToTarget from deps - it's called inside timeout

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isActive && currentStep) {
        const targetElement = document.getElementById(currentStep.targetId);
        if (targetElement) {
          const rect = targetElement.getBoundingClientRect();
          setTargetRect(rect);
          setCursorPosition({
            x: rect.left + rect.width / 2 - 12,
            y: rect.top + rect.height / 2 - 12,
          });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, currentStep]);

  // Block clicks outside the target element and tooltip
  useEffect(() => {
    if (!isActive || !isVisible || !targetRect || !currentStep) return;

    const handleClick = (e: MouseEvent) => {
      const clickX = e.clientX;
      const clickY = e.clientY;

      // Check if click is within target bounds (with some padding)
      const isWithinTarget =
        clickX >= targetRect.left - 10 &&
        clickX <= targetRect.right + 10 &&
        clickY >= targetRect.top - 10 &&
        clickY <= targetRect.bottom + 10;

      // Check if click is on the tooltip
      const target = e.target as HTMLElement;
      const isOnTooltip = target.closest('[data-guided-cursor-tooltip]') !== null;

      // Block click if not on target or tooltip
      if (!isWithinTarget && !isOnTooltip) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    // Capture clicks in the capture phase
    document.addEventListener('click', handleClick, true);
    document.addEventListener('mousedown', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mousedown', handleClick, true);
    };
  }, [isActive, isVisible, targetRect, currentStep]);

  if (!isActive || !isVisible || !targetRect) return null;

  // Calculate spotlight position relative to content area (below TopBar)
  const topBarHeight = 72; // TOP_BAR_HEIGHT numeric value
  const spotTop = Math.max(0, targetRect.top - 6 - topBarHeight);
  const spotBottom = Math.max(0, targetRect.bottom + 6 - topBarHeight);

  return createPortal(
    <>
      {/* Dark overlay with spotlight cutout - starts below TopBar, stops before right sidebar */}
      <Box
        position="fixed"
        top={TOP_BAR_HEIGHT}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        bottom={0}
        zIndex={9990}
        pointerEvents="none"
        bg="blackAlpha.600"
        style={{
          clipPath: `polygon(
            0% 0%,
            0% 100%,
            ${targetRect.left - 6}px 100%,
            ${targetRect.left - 6}px ${spotTop}px,
            ${targetRect.right + 6}px ${spotTop}px,
            ${targetRect.right + 6}px ${spotBottom}px,
            ${targetRect.left - 6}px ${spotBottom}px,
            ${targetRect.left - 6}px 100%,
            100% 100%,
            100% 0%
          )`,
        }}
      />

      {/* Spotlight highlight border around target */}
      <Box
        position="fixed"
        left={`${targetRect.left - 6}px`}
        top={`${targetRect.top - 6}px`}
        width={`${targetRect.width + 12}px`}
        height={`${targetRect.height + 12}px`}
        border="2px solid"
        borderColor="orange.400"
        borderRadius="8px"
        boxShadow="0 0 0 4px rgba(246, 133, 30, 0.2)"
        zIndex={9991}
        pointerEvents="none"
      />

      {/* Dark overlay for TopBar - must be higher z-index than TopBar (10000) */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        h={TOP_BAR_HEIGHT}
        zIndex={10500}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        pointerEvents="none"
      />

      {/* Dark overlay for Right Sidebar - covers entire sidebar height */}
      <Box
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        zIndex={10500}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        pointerEvents="none"
      />

      {/* Animated cursor */}
      <Box
        position="fixed"
        left={`${cursorPosition.x}px`}
        top={`${cursorPosition.y}px`}
        zIndex={10550}
        pointerEvents="none"
        transition="left 0.7s cubic-bezier(0.4, 0, 0.2, 1), top 0.7s cubic-bezier(0.4, 0, 0.2, 1)"
      >
        {/* Cursor icon */}
        <Box
          color="orange.500"
          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
          transform={showClickEffect ? 'scale(0.9)' : 'scale(1)'}
          transition="transform 0.1s ease"
        >
          <FaMousePointer size={24} />
        </Box>

        {/* Click ripple effect */}
        {showClickEffect && (
          <>
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              width="40px"
              height="40px"
              borderRadius="50%"
              bg="orange.400"
              opacity={0}
              sx={{
                animation: 'clickRipple 0.6s ease-out',
                '@keyframes clickRipple': {
                  '0%': {
                    transform: 'translate(-50%, -50%) scale(0)',
                    opacity: 0.6,
                  },
                  '100%': {
                    transform: 'translate(-50%, -50%) scale(2)',
                    opacity: 0,
                  },
                },
              }}
            />
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              width="20px"
              height="20px"
              borderRadius="50%"
              bg="orange.300"
              opacity={0}
              sx={{
                animation: 'clickRipple2 0.4s ease-out 0.1s',
                '@keyframes clickRipple2': {
                  '0%': {
                    transform: 'translate(-50%, -50%) scale(0)',
                    opacity: 0.8,
                  },
                  '100%': {
                    transform: 'translate(-50%, -50%) scale(1.5)',
                    opacity: 0,
                  },
                },
              }}
            />
          </>
        )}
      </Box>

      {/* Instruction tooltip */}
      <Box
        data-guided-cursor-tooltip
        position="fixed"
        left={`${Math.min(targetRect.right + 16, window.innerWidth - 300)}px`}
        top={`${Math.max(targetRect.top - 20, 20)}px`}
        zIndex={10600}
        pointerEvents="auto"
      >
        {/* Arrow pointing to target */}
        <Box
          position="absolute"
          left="-8px"
          top="20px"
          width={0}
          height={0}
          borderTop="8px solid transparent"
          borderBottom="8px solid transparent"
          borderRight="8px solid #ffffff"
        />
        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.2)"
          maxW="280px"
          overflow="hidden"
          position="relative"
        >
          {/* Close button - absolute positioned top-right */}
          <IconButton
            aria-label="Close"
            icon={<CloseIcon boxSize={2.5} />}
            position="absolute"
            top={2}
            right={2}
            size="xs"
            variant="ghost"
            color="gray.400"
            minW="24px"
            h="24px"
            onClick={onSkip}
            _hover={{ color: 'gray.600', bg: 'gray.100' }}
            zIndex={1}
          />

          {/* Content */}
          <VStack spacing={1} align="start" p={4} pr={10} pb={2}>
            <Text fontSize="sm" fontWeight="600" color="gray.800">
              {currentStep?.message}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {currentStep?.subMessage || 'Click to continue'}
            </Text>
          </VStack>

          {/* Footer with Skip, Back, and Next buttons */}
          <HStack
            justify="space-between"
            px={4}
            py={3}
            bg="gray.50"
            borderTop="1px solid"
            borderColor="gray.100"
          >
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="ghost"
                color="gray.500"
                fontWeight="500"
                onClick={onSkip}
                _hover={{ color: 'gray.700', bg: 'transparent' }}
                px={0}
              >
                Skip
              </Button>
              {currentStepIndex > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  color="gray.600"
                  fontWeight="500"
                  onClick={goToPreviousStep}
                  _hover={{ color: 'gray.800', bg: 'transparent' }}
                >
                  Back
                </Button>
              )}
            </HStack>
            <Button
              size="sm"
              colorScheme="orange"
              fontWeight="500"
              onClick={handleNextClick}
            >
              Next ({currentStepIndex + 1}/{steps.length})
            </Button>
          </HStack>
        </Box>
      </Box>
    </>,
    document.body
  );
};

export default GuidedCursor;
